import React from 'react'
import {Box, Typography} from '@mui/material'
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined'
import FaxOutlinedIcon from '@mui/icons-material/FaxOutlined'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import AddressBox, {AddressEntry} from 'src/components-new/data/AddressBox'
import AppDialog from 'src/components-new/feedback/AppDialog'
import ReadOnlyField from 'src/components-new/inputs/ReadOnlyField'
import StatusPill from 'src/components-new/primitives/StatusPill'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'
import {getMedicalCenterTypeLabel, MedicalCenterRow} from './medicalCentersColumns'

// Righe nell'ordine in cui comparirebbero su una busta: via e civico, località, CAP,
// città, provincia, regione, stato — stesso ordine di AnagraficaTab.tsx's buildAddressEntries.
function buildAddressEntries(medicalCenter: MedicalCenterRow): AddressEntry[] {
  const streetLine = [medicalCenter.address, medicalCenter.streetNumber].filter(Boolean).join(', ')
  const entries = [
    {label: 'Via', value: streetLine},
    {label: 'Località', value: medicalCenter.locality},
    {label: 'CAP', value: medicalCenter.postalCode},
    {label: 'Città', value: medicalCenter.city},
    {label: 'Provincia', value: medicalCenter.province},
    {label: 'Regione', value: medicalCenter.region},
    {label: 'Stato', value: medicalCenter.country},
  ]
  return entries.filter((entry): entry is AddressEntry => Boolean(entry.value))
}

interface MedicalCenterDetailDialogNewProps {
  medicalCenter: MedicalCenterRow
  open: boolean
  onClose: () => void
}

const FIELD_GRID_SX = {
  display: 'grid',
  gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)'},
  gap: 2,
} as const

const SECTION_TITLE_SX = {
  color: NEUTRAL_SCALE[900],
  fontWeight: 600,
  fontSize: 16,
  mb: 1.5,
} as const

// Icon + ReadOnlyField, so each contact channel (telefono/fax/email) is easier to
// scan than a bare grid of labels — same visual weight as ReadOnlyField elsewhere,
// just with a leading glyph identifying the channel.
function ContactField({icon, label, value}: {icon: React.ReactNode; label: string; value?: string}): JSX.Element {
  return (
    <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1}}>
      <Box sx={{color: NEUTRAL_SCALE[500], mt: '12px', display: 'flex'}}>{icon}</Box>
      <ReadOnlyField label={label} value={value} />
    </Box>
  )
}

/**
 * New-style, read-only equivalent of the legacy MedicalCenterDialog
 * (src/components/templates/MedicalCenterDialog/MedicalCenterDialog.tsx).
 */
export default function MedicalCenterDetailDialogNew({medicalCenter, open, onClose}: MedicalCenterDetailDialogNewProps): JSX.Element {
  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={medicalCenter.name ?? 'Centro medico'}
      // Same StatusPill used for "Tipo" in the medical centers list, next to the
      // title (the center's Nome) instead of buried in the Dati principali grid —
      // same placement ActivityDetailDialogNew uses for its own state pill.
      titleAction={<StatusPill label={getMedicalCenterTypeLabel(medicalCenter.type)} color="default" />}
      maxWidth="md"
    >
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 3}}>
        <Box>
          <Typography variant="h3" sx={SECTION_TITLE_SX}>Dati principali</Typography>
          <Box sx={FIELD_GRID_SX}>
            <ReadOnlyField label="Nome" value={medicalCenter.name} />
            <ReadOnlyField label="Codice" value={medicalCenter.code} />
          </Box>
        </Box>

        <Box>
          <Typography variant="h3" sx={SECTION_TITLE_SX}>Contatti</Typography>
          <Box sx={FIELD_GRID_SX}>
            <ContactField icon={<PhoneOutlinedIcon fontSize="small" />} label="Telefono" value={medicalCenter.phoneNumber} />
            <ContactField icon={<FaxOutlinedIcon fontSize="small" />} label="Fax" value={medicalCenter.faxNumber} />
            <ContactField icon={<EmailOutlinedIcon fontSize="small" />} label="Email" value={medicalCenter.email} />
          </Box>
        </Box>

        <Box>
          <Typography variant="h3" sx={SECTION_TITLE_SX}>Indirizzo</Typography>
          <AddressBox entries={buildAddressEntries(medicalCenter)} />
        </Box>
      </Box>
    </AppDialog>
  )
}
